import { DndContext, DragOverlay, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useState, useEffect, useCallback } from 'react';
import { useBuilderStore } from '../../stores/builderStore';
import { createField, fieldRegistry } from '../../lib/fieldRegistry';
import FieldPalette from './FieldPalette';
import FormCanvas from './FormCanvas';
import FieldConfigurator from './FieldConfigurator';
import FormPreview from './FormPreview';
import type { FieldType } from '../../types/form';

export default function Builder() {
  const { addField, reorderFields, setDragging, selectedFieldId, removeField, duplicateField } = useBuilderStore();
  const { undo, redo } = useBuilderStore.temporal.getState();
  const [, setActiveId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<FieldType | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Subscribe to temporal store changes
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    const unsubscribe = useBuilderStore.temporal.subscribe((state) => {
      setCanUndo(state.pastStates.length > 0);
      setCanRedo(state.futureStates.length > 0);
    });
    return unsubscribe;
  }, []);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Ignore if typing in an input field
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }

    // Ctrl/Cmd + Z: Undo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    }

    // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y: Redo
    if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      e.preventDefault();
      redo();
    }

    // Delete or Backspace: Remove selected field
    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFieldId) {
      e.preventDefault();
      removeField(selectedFieldId);
    }

    // Ctrl/Cmd + D: Duplicate selected field
    if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedFieldId) {
      e.preventDefault();
      duplicateField(selectedFieldId);
    }
  }, [selectedFieldId, removeField, duplicateField, undo, redo]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    setActiveId(active.id as string);
    setDragging(true);

    // Check if this is a new field from the palette
    if (active.data.current?.isNew) {
      setActiveType(active.data.current.type as FieldType);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);
    setActiveType(null);
    setDragging(false);

    if (!over) return;

    // Check if this is a new field from the palette
    if (active.data.current?.isNew && active.data.current?.type) {
      const fieldType = active.data.current.type as FieldType;
      const newField = createField(fieldType);

      // If dropped on an existing field, add after it
      if (over.id !== 'canvas' && typeof over.id === 'string') {
        addField(newField, over.id);
      } else {
        addField(newField);
      }
      return;
    }

    // Handle reordering existing fields
    if (active.id !== over.id) {
      reorderFields(active.id as string, over.id as string);
    }
  }

  function handleDragCancel() {
    setActiveId(null);
    setActiveType(null);
    setDragging(false);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {/* Toolbar with Undo/Redo */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border-r border-gray-200 pr-3 mr-3">
            <button
              onClick={() => undo()}
              disabled={!canUndo}
              className="p-2 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            <button
              onClick={() => redo()}
              disabled={!canRedo}
              className="p-2 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
              </svg>
            </button>
          </div>
          <div className="text-xs text-gray-500">
            <span className="font-medium">Shortcuts:</span> Delete key to remove, Ctrl+D to duplicate
          </div>
        </div>

        {/* Preview Button */}
        <button
          onClick={() => setIsPreviewOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Preview
        </button>
      </div>

      <div className="flex h-[calc(100vh-11rem)]">
        <FieldPalette />
        <FormCanvas />
        <FieldConfigurator />
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeType && (
          <div className="bg-white border-2 border-primary-500 rounded-lg p-4 shadow-lg opacity-90">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-600 rounded">
                {fieldRegistry[activeType].icon}
              </span>
              <span className="font-medium">{fieldRegistry[activeType].label}</span>
            </div>
          </div>
        )}
      </DragOverlay>

      {/* Form Preview Modal */}
      <FormPreview isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} />
    </DndContext>
  );
}
